import { useEffect, useState } from "react";
import axios from "axios";
import { Send, LoaderCircle } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
    "w-full rounded-lg border border-[#8A7563]/20 bg-[#FAF8F5]/70 px-4 py-3 text-sm text-[#2B2622] placeholder:text-[#8A7F72]/60 transition-colors focus:border-[#8A7563]/60 focus:outline-none";
const labelCls = "mb-2 block font-mono-accent text-[10px] uppercase tracking-[0.25em] text-[#8A7F72]";

const EMPTY = { name: "", email: "", phone: "", city: "", airbnb_link: "", message: "" };

export default function ContactForm({ prefill }) {
    const [form, setForm] = useState(EMPTY);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (prefill) setForm((f) => ({ ...f, message: prefill }));
    }, [prefill]);

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await axios.post(`${API}/contact`, { ...form, airbnb_link: form.airbnb_link || null });
            toast.success("Merci ! Votre demande est bien envoyée — nous vous recontactons sous 24 h.");
            setForm(EMPTY);
        } catch (err) {
            toast.error(err.response?.data?.detail?.[0]?.msg || "L'envoi a échoué. Vérifiez les champs et réessayez.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <section id="contact" data-testid="contact-section" className="mx-auto max-w-7xl px-6 py-28">
            <div className="grid grid-cols-1 gap-14 lg:grid-cols-[0.9fr_1.1fr]">
                <Reveal>
                    <p className="eyebrow mb-6 flex items-center gap-3">
                        <span className="inline-block h-px w-10 bg-[#8A7563]" />
                        Parlons de votre bien
                    </p>
                    <h2 className="font-display text-3xl font-semibold leading-tight sm:text-4xl lg:text-5xl">
                        Confiez-nous vos clés, <span className="italic text-[#8A7563]">récoltez vos revenus.</span>
                    </h2>
                    <p className="mt-6 max-w-md text-sm leading-relaxed text-[#8A7F72] md:text-base">
                        Étude gratuite et sans engagement de votre logement : potentiel locatif,
                        prix par nuit, projection annuelle. Réponse sous 24 heures ouvrées.
                    </p>
                    <div className="mt-10 space-y-4 text-sm text-[#5A4F44]">
                        <p><span className="font-mono-accent text-[10px] uppercase tracking-[0.25em] text-[#8A7563]">Téléphone</span><br />+1 438-397-6147<br />+1 263-382-6156</p>
                        <p><span className="font-mono-accent text-[10px] uppercase tracking-[0.25em] text-[#8A7563]">Email</span><br />info@gestionsejoura.com</p>
                        <p><span className="font-mono-accent text-[10px] uppercase tracking-[0.25em] text-[#8A7563]">Bureaux</span><br />Montréal — Le Plateau · Mont-Tremblant</p>
                    </div>
                </Reveal>

                <Reveal delay={0.15}>
                    <form
                        data-testid="contact-form"
                        onSubmit={submit}
                        className="rounded-2xl border border-[#2B2622]/10 bg-[#FFFFFF]/70 p-8 backdrop-blur-xl"
                    >
                        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                            <label className="block">
                                <span className={labelCls}>Nom complet *</span>
                                <input data-testid="contact-name-input" required minLength={2} value={form.name} onChange={set("name")} placeholder="Marie Dupont" className={inputCls} />
                            </label>
                            <label className="block">
                                <span className={labelCls}>Email *</span>
                                <input data-testid="contact-email-input" required type="email" value={form.email} onChange={set("email")} placeholder="marie@exemple.fr" className={inputCls} />
                            </label>
                            <label className="block">
                                <span className={labelCls}>Téléphone *</span>
                                <input data-testid="contact-phone-input" required minLength={6} value={form.phone} onChange={set("phone")} placeholder="06 12 34 56 78" className={inputCls} />
                            </label>
                            <label className="block">
                                <span className={labelCls}>Ville du bien *</span>
                                <input data-testid="contact-city-input" required minLength={2} value={form.city} onChange={set("city")} placeholder="Montréal, Tremblant, Québec…" className={inputCls} />
                            </label>
                        </div>
                        <label className="mt-5 block">
                            <span className={labelCls}>Lien de votre annonce Airbnb (optionnel)</span>
                            <input data-testid="contact-airbnb-input" value={form.airbnb_link} onChange={set("airbnb_link")} placeholder="https://www.airbnb.fr/rooms/…" className={inputCls} />
                        </label>
                        <label className="mt-5 block">
                            <span className={labelCls}>Votre message *</span>
                            <textarea
                                data-testid="contact-message-input"
                                required
                                minLength={10}
                                rows={5}
                                value={form.message}
                                onChange={set("message")}
                                placeholder="Décrivez votre bien : type, surface, situation, vos disponibilités…"
                                className={`${inputCls} resize-none`}
                            />
                        </label>
                        <button
                            data-testid="contact-submit-button"
                            type="submit"
                            disabled={loading}
                            className="group mt-7 flex w-full items-center justify-center gap-3 rounded-full bg-[#8A7563] px-7 py-4 text-sm font-semibold text-[#FAF8F5] transition-all duration-300 hover:bg-[#6E5D4E] hover:shadow-[0_0_32px_rgba(138,117,99,0.35)] disabled:opacity-60"
                        >
                            {loading ? <LoaderCircle size={16} className="animate-spin" /> : <Send size={15} className="transition-transform duration-300 group-hover:translate-x-1" />}
                            {loading ? "Envoi en cours…" : "Envoyer ma demande d'étude gratuite"}
                        </button>
                        <p className="mt-4 text-center text-xs text-[#8A7F72]">
                            Vos données servent uniquement à vous recontacter. Aucune prospection.
                        </p>
                    </form>
                </Reveal>
            </div>
        </section>
    );
}
